import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentUser } from "@/hooks/platform/useCurrentUser";
import { getOrCreateConversation } from "@/hooks/platform/useMessaging";
import { pushNotification } from "@/hooks/platform/useNotifications";

export interface AgencyMember {
  id: string;
  portal_id: string | null;
  username: string;
  full_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  user_type: string | null;
  verification_status: string | null;
  verification_submitted_at: string | null;
  verification_documents: Record<string, { label: string; name: string; url: string }> | null;
  business_name: string | null;
  business_registration: string | null;
  phone: string | null;
  city: string | null;
  country: string | null;
  address: string | null;
  profile_views: number | null;
  subscription_status: string | null;
  created_at: string | null;
}

/** Member details include private fields, so they are read through a staff-only secure lookup. */
const agencyMemberRpc = () =>
  (supabase.rpc as unknown as (fn: string) => Promise<{ data: unknown[] | null; error: Error | null }>)(
    "get_agency_member_profiles",
  );

export const useAgencyMembers = (userType?: "creator" | "venue" | "groover") =>
  useQuery({
    queryKey: ["agency-members", userType ?? "all"],
    queryFn: async () => {
      const { data, error } = await agencyMemberRpc();
      if (error) throw error;
      const rows = (data ?? []) as unknown as AgencyMember[];
      const filtered = userType ? rows.filter((r) => r.user_type === userType) : rows;
      return filtered
        .sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""))
        .slice(0, 500);
    },
  });

export const useVerificationQueue = () =>
  useQuery({
    queryKey: ["agency-verifications"],
    queryFn: async () => {
      const { data, error } = await agencyMemberRpc();
      if (error) throw error;
      return ((data ?? []) as unknown as AgencyMember[])
        .filter((r) => r.verification_status && r.verification_status !== "unverified")
        .sort((a, b) => (b.verification_submitted_at ?? "").localeCompare(a.verification_submitted_at ?? ""))
        .slice(0, 300);
    },
  });

export const useSetVerificationStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: "verified" | "required" | "in_review" }) => {
      const { error } = await supabase
        .from("profiles")
        .update({
          verification_status: status,
          verified_at: status === "verified" ? new Date().toISOString() : null,
        })
        .eq("id", id);
      if (error) throw error;
      return { id, status };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agency-verifications"] });
      qc.invalidateQueries({ queryKey: ["agency-members"] });
    },
  });
};

export interface AgencyBooking {
  id: string;
  creator_id: string;
  venue_id: string;
  event_date: string;
  status: string;
  amount: number | null;
  currency: string | null;
  title: string | null;
  notes: string | null;
  created_at: string;
}

export const useAgencyBookings = () =>
  useQuery({
    queryKey: ["agency-bookings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bookings")
        .select("id, creator_id, venue_id, event_date, status, amount, currency, title, notes, created_at")
        .order("event_date", { ascending: false })
        .limit(300);
      if (error) throw error;
      return (data ?? []) as AgencyBooking[];
    },
  });

export const useAgencyEvents = () =>
  useQuery({
    queryKey: ["agency-events"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("events")
        .select("id, title, owner_id, event_date, venue_name, city, status, capacity, flyer_url")
        .order("event_date", { ascending: false })
        .limit(200);
      if (error) throw error;
      return data ?? [];
    },
  });

/** The signed-in staff member's portal identifier. */
export const useMyPortalId = () => {
  const { userId } = useCurrentUser();
  return useQuery({
    queryKey: ["my-portal-id", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase.from("profiles").select("portal_id, username").eq("id", userId!).maybeSingle();
      if (error) throw error;
      return data as { portal_id: string | null; username: string } | null;
    },
  });
};

/* ------------------------------------------------------------------ */
/* Agency ↔ Creator representation (real data, no mocks)               */
/* ------------------------------------------------------------------ */

export interface AgencyStaffProfile {
  id: string;
  full_name: string | null;
  username: string;
  avatar_url: string | null;
  portal_id: string | null;
  verification_status: string | null;
  city: string | null;
  country: string | null;
  bio: string | null;
  user_type: string | null; // "creator_management" | "venue_oversight"
}

/**
 * Real agency staff directory (Creator Management + Venue Oversight portal users).
 * Served by the `get_agency_directory` database function, which exposes only safe,
 * non-sensitive columns so any signed-in creator or venue can discover agencies.
 */
export const useAgencyStaffDirectory = () =>
  useQuery({
    queryKey: ["agency-staff-directory"],
    queryFn: async () => {
      const rpc = supabase.rpc as unknown as (
        fn: string,
      ) => Promise<{ data: AgencyStaffProfile[] | null; error: { message: string } | null }>;
      const { data, error } = await rpc("get_agency_directory");
      if (error) throw new Error(error.message);
      return (data ?? []) as AgencyStaffProfile[];
    },
  });


/** The signed-in creator's own bookings — used for the Overview + Contracts tabs. */
export const useMyBookings = (creatorId?: string | null) =>
  useQuery({
    queryKey: ["my-bookings", creatorId],
    enabled: !!creatorId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bookings")
        .select("id, creator_id, venue_id, event_date, status, amount, currency, title, notes, created_at")
        .eq("creator_id", creatorId!)
        .order("event_date", { ascending: false });
      if (error) throw error;
      return (data ?? []) as AgencyBooking[];
    },
  });

/**
 * Whether the signed-in creator already has a live relationship (bookings or an
 * open conversation) with any agency staff member — used to derive representation
 * status instead of a fake "currently represented" flag.
 */
export const useMyRepresentationStatus = (userId?: string | null, staffIds: string[] = []) =>
  useQuery({
    queryKey: ["my-representation-status", userId, staffIds.sort().join(",")],
    enabled: !!userId,
    queryFn: async () => {
      const [bookingsRes, conversationsRes] = await Promise.all([
        supabase.from("bookings").select("id, venue_id, status").eq("creator_id", userId!).limit(1),
        supabase
          .from("conversations")
          .select("id, participant_a, participant_b")
          .or(`participant_a.eq.${userId},participant_b.eq.${userId}`),
      ]);
      if (bookingsRes.error) throw bookingsRes.error;
      if (conversationsRes.error) throw conversationsRes.error;

      const hasBookingWithStaff = staffIds.length > 0 && (bookingsRes.data ?? []).some((b) => staffIds.includes(b.venue_id));
      const conversationWithStaff = (conversationsRes.data ?? []).find(
        (c) => staffIds.includes(c.participant_a) || staffIds.includes(c.participant_b),
      );
      const hasAnyBooking = (bookingsRes.data ?? []).length > 0;

      if (hasBookingWithStaff) return { status: "represented" as const, staffId: null };
      if (conversationWithStaff)
        return {
          status: "pending" as const,
          staffId: conversationWithStaff.participant_a === userId ? conversationWithStaff.participant_b : conversationWithStaff.participant_a,
        };
      return { status: hasAnyBooking ? "independent" as const : "none" as const, staffId: null };
    },
  });

/** Sends a representation request to an agency staff member: opens/creates a conversation and notifies them. */
export const useRequestRepresentation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ meId, meName, staffId }: { meId: string; meName: string; staffId: string }) => {
      const conversation = await getOrCreateConversation(meId, staffId);
      const body = `${meName} has requested representation and would like to connect.`;
      await supabase.from("messages").insert({ conversation_id: conversation.id, sender_id: meId, body });
      await supabase
        .from("conversations")
        .update({ last_message: body, last_message_at: new Date().toISOString() })
        .eq("id", conversation.id);
      await pushNotification({
        userId: staffId,
        type: "representation_request",
        title: "New representation request",
        body,
        link: "/messages",
        metadata: { requester_id: meId, requester_name: meName },
      });
      return conversation;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["conversations"] });
      qc.invalidateQueries({ queryKey: ["my-representation-status"] });
    },
  });
};

/** Incoming representation requests for the signed-in agency staff member (Creator Management / Venue Oversight). */
export const useIncomingRepresentationRequests = (staffUserId?: string | null) =>
  useQuery({
    queryKey: ["incoming-representation-requests", staffUserId],
    enabled: !!staffUserId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        .eq("user_id", staffUserId!)
        .eq("type", "representation_request")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
